import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    fetchServicesThunk,
    saveServiceThunk,
    deleteServicesThunk,
    activateServicesThunk
} from 'actions/services/ServicesActions';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { Service, Meta, SERVICES_BUSINESS_AREAS } from 'model/Service';
import { generateUniqueCopyName } from 'utils/ServiceUtils';
// Import optimized selectors
import {
    getDaycareServices,
    getDaycareMeta,
    getDaycareLoading,
    getDaycareHasInitialized,
    getServicesStatus,
    getSavedService
} from 'reducers/services/ServicesSelectors';

export const DEFAULT_PAGE_SIZE = 10;
export const DEFAULT_PAGE = 1;

export interface UseDaycareServicesReturn {
    services: Service[];
    loading: boolean;
    errorSave: boolean;
    savedService?: Service;
    hasInitialized: boolean;
    meta: Meta | null;
    // CRUD Operations
    fetchDaycareServices: (
        page?: number,
        per?: number,
        status?: string,
        searchText?: string,
        marketplaceIds?: Array<number>
    ) => void;
    saveDaycareService: (service: Service, changedName?: boolean) => void;
    duplicateDaycareService: (service: Service) => void;
    deleteDaycareService: (service: Service) => void;
    toggleDaycareServiceStatus: (
        service: Service,
        refreshParams?: {
            page?: number;
            per?: number;
            status?: string;
            searchText?: string;
            marketplaceIds?: Array<number>;
        }
    ) => void;
}

export const useDaycareServices = (): UseDaycareServicesReturn => {
    const dispatch = useDispatch();

    // Use intelligent selectors - no more local state needed!
    const daycareServices = useSelector(getDaycareServices);
    const daycareMeta = useSelector(getDaycareMeta);
    const daycareLoading = useSelector(getDaycareLoading);
    const hasInitialized = useSelector(getDaycareHasInitialized);
    const servicesStatus = useSelector(getServicesStatus);
    const savedService = useSelector(getSavedService);

    const errorSave = servicesStatus === ServicesStatus.Error;

    const fetchDaycareServices = (
        page: number = DEFAULT_PAGE,
        per: number = DEFAULT_PAGE_SIZE,
        status = 'active',
        searchText = '',
        marketplaceIds?: Array<number>
    ) => {
        // Segmented architecture, no tracking needed - data goes directly to daycare slice
        dispatch(
            fetchServicesThunk(
                undefined,
                page,
                per,
                status,
                'asc',
                searchText,
                'daycare',
                marketplaceIds?.length ? marketplaceIds : undefined
            )
        );
    };

    const saveDaycareService = (service: Service) => {
        dispatch(saveServiceThunk(service));
    };

    const duplicateDaycareService = (service: Service) => {
        const existingNames = daycareServices.map(s => s.name);
        const uniqueName = generateUniqueCopyName(service.name, existingNames);

        const duplicatedService = {
            ...service,
            id: undefined, // Remove ID for new service
            uuid: '', // Will be generated by backend
            name: uniqueName
        };
        dispatch(saveServiceThunk(duplicatedService));
    };

    const deleteDaycareService = (service: Service) => {
        if (service.id) {
            dispatch(deleteServicesThunk([service.id]));
        }
    };

    const toggleDaycareServiceStatus = (
        service: Service,
        refreshParams?: {
            page?: number;
            per?: number;
            status?: string;
            searchText?: string;
            marketplaceIds?: Array<number>;
        }
    ) => {
        if (service.id) {
            const isActive = service.status.id === 1;

            // Custom callback without tracking - direct daycare refresh
            const onSuccessCallback = () => {
                const params = refreshParams || {
                    page: DEFAULT_PAGE,
                    per: DEFAULT_PAGE_SIZE,
                    status: 'active',
                    searchText: ''
                };

                // Direct daycare fetch - no tracking needed
                dispatch(
                    fetchServicesThunk(
                        undefined,
                        params.page,
                        params.per,
                        params.status,
                        'asc',
                        params.searchText,
                        SERVICES_BUSINESS_AREAS.DAYCARE,
                        params.marketplaceIds?.length ? params.marketplaceIds : undefined
                    )
                );
            };

            dispatch(activateServicesThunk([service.id], !isActive, onSuccessCallback));
        }
    };

    return {
        services: daycareServices,
        loading: daycareLoading, //Use daycare-specific loading
        errorSave,
        savedService,
        hasInitialized,
        meta: daycareMeta, // Use daycare-specific meta
        fetchDaycareServices,
        saveDaycareService,
        duplicateDaycareService,
        deleteDaycareService,
        toggleDaycareServiceStatus
    };
};
